<template>
  <div v-if="transactions.length" class="transaction-container">
    <div class="sub-header-text">TRANSACTION DETAILS</div>
    <Table>
      <thead>
        <tr>
          <th>Date</th>
          <th>Method</th>
          <th class="text-center">Type</th>
          <th class="text-right">Amount</th>
          <th>Reference Id</th>
          <th class="note-header">Notes</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="transaction in transactions"
          :key="transaction.id"
          :class="{ void: isVoid(transaction) }"
        >
          <td class="date-column">
            {{ formatTransactionDate(transaction.transaction_time) }}
          </td>
          <td class="method-container">
            <component
              :is="cardImageSrc(transaction)"
              v-if="isCard(transaction) && cardImageSrc(transaction)"
              class="card-img"
            />
            {{ displayTransactionMethod(transaction) }}
          </td>
          <td class="text-center">
            {{ displayTransactionType(transaction) }}
          </td>
          <td class="text-right">
            {{ formatCents(appliedCents(transaction)) }}
            <div
              v-if="transaction.covered_invoice_count > 1"
              class="multi-invoice-count"
              :title="`This payment covers invoices ${(
                transaction.covered_invoice_ids || []
              ).join(', ')}`"
            >
              1 of {{ transaction.covered_invoice_count }} invoices
            </div>
          </td>
          <td>
            {{ transaction.reference_id }}
            <i
              v-if="!isRefund(transaction) && isEditableReference(transaction)"
              class="fas fa-edit edit-icon text-link-color"
              @click="handleReferenceIdEdit(transaction)"
            />
          </td>
          <td class="transaction-notes">
            <div v-if="isFailed(transaction)" class="text-danger notes-content">
              <i
                class="fas fa-exclamation-circle warning-icon"
                title="Failed"
              />
              <span>{{ transaction.notes || emptyDisplay }}</span>
            </div>
            <div v-else class="notes-content">
              {{ transaction.notes || emptyDisplay }}
              <i
                class="fas fa-edit edit-icon text-link-color"
                @click="handleNoteEdit(transaction)"
              />
            </div>
          </td>
        </tr>
      </tbody>
    </Table>
  </div>
</template>

<script lang="ts">
import Table from "serviceshift-ui/components/General/Table.vue";
import amexSrc from "serviceshift-ui/shared/src/assets/amex.svg";
import discoverSrc from "serviceshift-ui/shared/src/assets/discover.svg";
import masterCardSrc from "serviceshift-ui/shared/src/assets/mastercard.svg";
import visaSrc from "serviceshift-ui/shared/src/assets/visa.svg";
import EventBus from "serviceshift-ui/shared/src/lib/event_bus";
import {
  capitalize,
  dollars,
  emptyDisplay
} from "serviceshift-ui/shared/src/mixins";

import {
  Transaction,
  TransactionStatus,
  TransactionType
} from "@/typings/transaction";

export default {
  components: {
    Table
  },
  props: {
    transactions: {
      type: Array as () => Transaction[],
      default: () => []
    }
  },
  data: function () {
    return {
      emptyDisplay
    };
  },
  methods: {
    appliedCents(transaction: any) {
      // Multi-invoice payments carry the share applied to this invoice
      return transaction.applied_amount_cents ?? transaction.amount_cents;
    },
    formatTransactionDate(date) {
      return new Date(date)
        .toLocaleString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        })
        .replace(",", "");
    },
    isCard(transaction: Transaction) {
      return transaction.transaction_type === TransactionType.CARD;
    },
    isEditableReference(transaction: Transaction) {
      return [
        TransactionType.EXTERNAL,
        TransactionType.EXTERNAL_FINANCING,
        TransactionType.CHECK
      ].includes(transaction.transaction_type);
    },
    isRefund(transaction: Transaction) {
      return transaction.amount_cents < 0;
    },
    cardImageSrc(transaction: Transaction) {
      const cardBrand = transaction.card_brand || "";
      switch (cardBrand.toLowerCase()) {
        case "discover":
          return discoverSrc;
        case "visa":
          return visaSrc;
        case "mastercard":
          return masterCardSrc;
        case "american express":
        case "amex":
          return amexSrc;
        default:
          return "";
      }
    },
    formatCents(cents) {
      return dollars(cents);
    },
    displayTransactionMethod(transaction: Transaction) {
      const cardNumber = transaction.card_last4
        ? `**** ${transaction.card_last4}`
        : "****";

      switch (transaction.transaction_type) {
        case TransactionType.CHECK:
          return this.isRefund(transaction) ? `Check/Cash` : "Check";
        case TransactionType.CARD:
          return `${cardNumber}${
            transaction.entry_method ? ` (${transaction.entry_method})` : ""
          }`;
        case TransactionType.ACH:
          return `${capitalize(transaction.ach_account_type)} ACH ...${
            transaction.ach_last4
          }`;
        case TransactionType.EXTERNAL:
          return `External Payment: ${transaction.processor_name}`;
        case "external_financing":
          return `External Financing: ${transaction.processor_name} (Loan #${transaction.reference_id})`;
        default:
          return "Paid in Cash";
      }
    },
    displayTransactionType(transaction: Transaction) {
      return this.isRefund(transaction) ? "Refund" : "Payment";
    },
    handleNoteEdit(transaction: Transaction) {
      EventBus.$emit(
        "edit-transaction-note-click",
        transaction,
        transaction.invoice_id
      );
    },
    handleReferenceIdEdit(transaction: Transaction) {
      EventBus.$emit(
        "edit-transaction-reference-id-click",
        transaction,
        transaction.invoice_id
      );
    },
    isVoid(transaction: Transaction) {
      return transaction.status === TransactionStatus.VOIDED;
    },
    isFailed(transaction: Transaction) {
      return (
        transaction.status === TransactionStatus.FAILED ||
        transaction.status === TransactionStatus.ATTEMPTED
      );
    }
  }
};
</script>

<style lang="scss" scoped>
.multi-invoice-count {
  color: #6c757d;
  font-size: 0.75rem;
  white-space: nowrap;
}

.transaction-container {
  margin-bottom: 1rem;
  margin-top: 1rem;
}

tbody tr td {
  vertical-align: top;
  text-align: left;
  font-size: 13px;
  line-height: 16px;
}

thead tr th {
  text-align: left;
}

.sub-header-text {
  font-size: 14px;
  font-weight: bolder;
  line-height: 16px;
  margin-bottom: 14px;
}

.note-header {
  width: 50%;
}

.transaction-notes {
  img {
    width: 15px;
    height: 15px;
    vertical-align: text-top;
  }
}

.edit-icon {
  font-size: 1em;
  margin-left: 5px;
  cursor: pointer;
}

.warning-icon {
  font-size: 1em;
  margin-right: 5px;
  margin-left: 0px;
  cursor: none;
}

.method-container {
  text-align: left;
  white-space: nowrap;
  img {
    vertical-align: middle;
  }
}

.card-img {
  display: inline-block;
  margin-right: 5px;
  width: 35px;
  height: 25px;
}

.void {
  text-decoration: line-through;
  font-style: italic;
}

.text-danger {
  color: var(--danger-color);
}

.notes-content {
  word-wrap: break-word;
  overflow-wrap: break-word;
  white-space: normal !important;
}

.date-column {
  white-space: nowrap;
}
</style>
